import * as THREE from 'three';
import { atriumLayout, ATRIUM_RADIUS, hexToWorld, naveLayout, naveTrace, NAVE_RADIUS, type AtriumLayout, type BattleState, type MapDef, type NaveLayout } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { naveWalls } from './ermita-nave.ts';
import type { OmenCtx } from '../omens/common.ts';
import { CaminoLayer, HOURS, type Hour } from './camino.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The cleric's last road, chapter 4 (docs/sendas/clerigo.md, «La Ermita»), on chapter 1's craft (engine/camino.ts):
 * the Hermitage of the Crag, a chapel cut in the cliff high on the Spine's south face.
 *
 * - `ermitaDawn` (the cinematic) and `ermitaDoor` (the lowest point, the decision): the cliff's face with the Hermitage
 *   carved in it, pale stone; its door broken, ink running down the facade; the tower out over nothing with its great
 *   lantern dark at the top; the nave's roof broken open; the snowy way up to it, a brazier by the door.
 * - `naveNow` (the test, and Aud's mirror found), `naveMemory` (the memory, sixty-two years before), `navePast` (its
 *   dawn) and `naveClose` (the close): the nave, a long hall cut in the rock, north to south. Walls of pale carved stone
 *   with niches; the altar at the north end, the loose stone behind it; the nave's door at the south end; the columns
 *   the six great mirrors (polished steel discs in iron yokes that turn) and the two dividers (panes of glass on a
 *   bracket) stand on, their numbers cut on the column's foot; the broken beam over the middle under the hole in the
 *   roof, the lantern hung from it, a shaft of pale light and snow coming down; the ink on the walls, breathing violet,
 *   where the limpets come out; the beam on the floor (faint where it went through two dividers), the altar and the
 *   door lit with a flash when it reaches both. In the memory the roof is whole, the walls clean, and the vigil's six
 *   lamps stand up the nave (clay lamps on iron stands): dark, then lit with a warm-white flame and a ring of light;
 *   the Doliente's tongues of ink run along the floor from her to the lamp they reach for; her wail darkens the nave.
 * - `refectory` (the campfire): the Hermitage's refectory, a long table and benches, the hearth.
 * - `ermitaAtrium` (act 1) is a crypt's descent re-dressed (AtriumCrypt, below).
 *
 * Chapter 5 (battle/vigilia.ts), on chapter 4's nave (the one won: `naveSeed`):
 * - `naveVigil` (the lamps, the mirrors again, the Doliente, and their scenes): the nave now, by night, snow coming in by
 *   the hole; the six clay lamps of the vigil up the nave on their iron stands, each with its ring of light once lit;
 *   the candle on its stone by Tessa (smaller after each wail not cut; out, a curl of smoke); the brazier by the mirror
 *   that frosts over (the mirror in a crust of ice until thawed); the first light: the shaft through the hole goes white
 *   and the beam goes from the lantern's cold blue to the sun's white, wider.
 * - `naveLit` (the scenes after the lamps: the letters, the campfire, the speech): the same nave, its six lamps lit.
 * - `naveDawn` (the close): the morning in the nave, the sun down the hole and along the way to the altar and the door,
 *   the six lamps still lit.
 * - `atriumNight` (the atrium, act 2): the hall between the cliff's door and the nave's, cut in the rock: four great
 *   pillars, the cells' arches in the long walls, niches with candle stubs, the pilgrims' bones by the walls; the nave's
 *   door shut and barred with planks (Piet behind it); the cliff's door open on the snow and the night.
 * - `ermitaTomb` (the epilogue): the cliff in the morning, a small grave at the foot of the tower with an uncut stone,
 *   the vigil's lamps set round it.
 *
 * Draw calls on top of chapter 1's: the dressing (one merged mesh), the mirrors and dividers (a group each), the beam
 * and the tongues (a strip mesh each, rebuilt only when they change: the tongues at the sim's 12 Hz), the shaft of light
 * (one), the lamps' rings (one instanced). The flames are the glow layer's; the board's one point light is the
 * lantern (or, in the memory, the altar's candles) (docs/RENDIMIENTO.md).
 */

export const ERMITA_PLACES = new Set(['naveNow', 'naveMemory', 'navePast', 'naveClose', 'refectory', 'ermitaDoor', 'ermitaDawn', 'naveVigil', 'naveLit', 'naveDawn', 'atriumNight', 'ermitaTomb']);
const NAVES = new Set(['naveNow', 'naveMemory', 'navePast', 'naveClose', 'naveVigil', 'naveLit', 'naveDawn']);
/** Chapter 5's naves: the lamps of the vigil stand up them, the candle by the altar. */
const VIGIL = new Set(['naveVigil', 'naveLit', 'naveDawn']);
/** The nave the scenes stand in (the figures of content/camino.ts are placed on it). */
export const NAVE_SCENE_SEED = 4;

const S = WORLD_PER_HEX;
const K = S / 1000;
const WHITE = new THREE.Color('#f4f8ff');
const COLD = new THREE.Color('#c4d4ff');
const WARM = new THREE.Color('#ffe6d2');
const FLAME = new THREE.Color('#fff2e6');
const INK = new THREE.Color('#6a4aa8');
const RED = new THREE.Color('#ff4f3a');
const STONE = ['#8f949c', '#868b93', '#989da5'];
const STONE_D = ['#6d727a', '#656a72', '#757a82'];
const FLAG = ['#7d828a', '#747981', '#858a92'];
const SNOW = ['#c8d3e1', '#bccadb', '#d3dcea'];
const IRON = '#2a2d32';
/** The Doliente's tongues: room for this many at once, each in this many pieces. */
const TONGUES = 16;
const TONGUE_N = 6;

/** The close's nave starts in the sun (chapter 5). */
const p0Sun = (place0: string): number => (place0 === 'naveDawn' ? 1 : 0);

const allCells = (R: number): { q: number; r: number }[] => {
  const out: { q: number; r: number }[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) <= R) out.push({ q, r });
  return out;
};
const key = (q: number, r: number): string => `${q},${r}`;

/** The scenes' own boards (game/cine.ts): the nave of NAVE_SCENE_SEED, the refectory, the cliff by the Hermitage. */
export const ermitaSceneMap = (place0: string): MapDef | null => {
  if (!ERMITA_PLACES.has(place0) || place0 === 'naveMemory') return null;
  if (NAVES.has(place0)) {
    const L = naveLayout(NAVE_SCENE_SEED);
    return { radius: NAVE_RADIUS + 2, bastion: NAVE_RADIUS, sectors: [], kind: 'incursion', open: true, rocks: L.rocks };
  }
  if (place0 === 'atriumNight') {
    const A = atriumLayout(1);
    return { radius: ATRIUM_RADIUS + 2, bastion: ATRIUM_RADIUS, sectors: [], kind: 'incursion', open: true, rocks: A.rocks };
  }
  const R = 6;
  const rocks = allCells(R).filter((h) => {
    const p = hexToWorld(h.q, h.r);
    if (place0 === 'refectory') return Math.max(Math.abs(h.q), Math.abs(h.r), Math.abs(h.q + h.r)) >= 5;
    // The cliff to the north; the way up to the door narrow between drifts.
    return h.r <= -3 || (h.r >= 1 && Math.abs(p.x) > 2600);
  });
  return { radius: R + 2, bastion: R, sectors: [], kind: 'incursion', open: true, rocks };
};

interface PieceView {
  g: THREE.Group;
  head: THREE.Group;
  yaw: number;
  want: number;
  at: THREE.Vector3;
  spin: number;
  split: boolean;
}

interface Er {
  L: NaveLayout | null;
  past: boolean;
  pieces: PieceView[];
  lamps: THREE.Vector3[];
  lampLit: number[];
  lampFlash: number[];
  rings: THREE.InstancedMesh | null;
  ink: THREE.Vector3[];
  shaft: THREE.Mesh | null;
  shaftMat: THREE.ShaderMaterial | null;
  lantern: THREE.Group | null;
  lanternAt: THREE.Vector3 | null;
  altarAt: THREE.Vector3 | null;
  doorAt: THREE.Vector3 | null;
  beam: THREE.Mesh | null;
  beamMat: THREE.ShaderMaterial | null;
  beamKey: string;
  tongues: THREE.Mesh | null;
  tongueMat: THREE.ShaderMaterial | null;
  tongueKey: string;
  both: number;
  altarLit: number;
  doorLit: number;
  wail: number;
  wailWarn: number;
  audSlot: number;
  stone: THREE.Vector3 | null;
  hearth: THREE.Vector3 | null;
  dawn: number;
  /** Chapter 5: the candle by Tessa (where, how big, lit), the sun on the way, the frosted mirror's crust, the brazier. */
  candle: THREE.Vector3 | null;
  candleSize: number;
  candleLit: number;
  sun: number;
  frost: THREE.Mesh[];
  frostOn: number;
  brazier: THREE.Vector3 | null;
  brazierLit: number;
  /** The atrium: its two doors, the bars on the nave's (shaking when struck). */
  atrium: AtriumLayout | null;
  bars: THREE.Group | null;
  barsHit: number;
  /** The epilogue's grave and its lamps. */
  grave: THREE.Vector3[];
}

export class ErmitaLayer extends CaminoLayer {
  declare private er: Er;

  constructor(ctx: OmenCtx, map: MapDef, place0: string, s: BattleState) {
    super(ctx, map, place0, s);
    this.group.name = `ermita-${place0}`;
  }

  protected override buildOwn(kit: Kit, s: BattleState): boolean {
    this.er = {
      L: null,
      past: this.place === 'naveMemory' || this.place === 'navePast',
      pieces: [],
      lamps: [],
      lampLit: [],
      lampFlash: [],
      rings: null,
      ink: [],
      shaft: null,
      shaftMat: null,
      lantern: null,
      lanternAt: null,
      altarAt: null,
      doorAt: null,
      beam: null,
      beamMat: null,
      beamKey: '',
      tongues: null,
      tongueMat: null,
      tongueKey: '',
      both: 0,
      altarLit: 0,
      doorLit: 0,
      wail: 0,
      wailWarn: 0,
      audSlot: -1,
      stone: null,
      hearth: null,
      dawn: 0,
      candle: null,
      candleSize: 1,
      candleLit: 1,
      sun: p0Sun(this.place),
      frost: [],
      frostOn: -1,
      brazier: null,
      brazierLit: 0,
      atrium: null,
      bars: null,
      barsHit: 0,
      grave: [],
    };
    const p = this.place;
    if (NAVES.has(p)) {
      this.inside = true;
      this.buildNave(kit, s);
    } else if (p === 'refectory') {
      this.inside = true;
      this.buildRefectory(kit);
    } else if (p === 'atriumNight') {
      this.inside = true;
      this.buildAtrium(kit);
    } else {
      this.buildCliff(kit, p === 'ermitaDawn');
      if (p === 'ermitaTomb') this.buildGrave(kit);
    }
    return true;
  }

  /* ---------- the nave ---------- */

  private buildNave(kit: Kit, s: BattleState): void {
    // (Chapter 5 is played on chapter 4's nave, the one won: its seed rides in the rules.)
    const L = naveLayout(s.rules?.senda?.litter?.hermit?.naveSeed ?? s.seed);
    this.er.L = L;
    const past = this.er.past;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    const floor = new Set<string>();
    const R = L.radius;
    for (const h of allCells(R)) if (!L.rocks.some((x) => x.q === h.q && x.r === h.r)) floor.add(key(h.q, h.r));
    const cols = new Set<string>([...L.mirrors, ...L.splits].map((h) => key(h.q, h.r)));
    // The walls: every rock cell by the floor (not a column, not the altar) is carved stone, tall to the north, lower to
    // the south (the miniature's cut-away: the camera looks in from the door's side).
    const D: [number, number][] = [
      [1, 0],
      [1, -1],
      [0, -1],
      [-1, 0],
      [-1, 1],
      [0, 1],
    ];
    for (const h of L.rocks) {
      if (cols.has(key(h.q, h.r)) || (h.q === L.altar.q && h.r === L.altar.r)) continue;
      const by = D.some(([dq, dr]) => floor.has(key(h.q + dq, h.r + dr)));
      const p = this.hex(h.q, h.r);
      // The rock beyond the walls: dark and low, so the eye stays in the nave; by the floor a dark mass up under the
      // walls' cornice (the walls themselves are one carved hall along the floor's outline: engine/ermita-nave.ts).
      // (By the floor nothing: half of such a cell stood out past the wall's face, a dark block on the bench.)
      if (by) continue;
      kit.add(box, '#23262c', place(p.x, p.y + 0.25, p.z, 0, new THREE.Vector3(1.16, 0.5, 1.16)));
    }
    const lc0 = this.hex(L.lantern.q, L.lantern.r);
    const walls = naveWalls(kit, L, lc0.y, (pp, c, size, a, flick) => this.light(pp, c, size, a, flick), { past, flame: FLAME, cold: COLD });
    // The columns with their mirrors and dividers; each column's number cut on its foot.
    [...L.mirrors, ...L.splits].forEach((h, i) => {
      const p = this.hex(h.q, h.r);
      kit.add(box, STONE_D[0] as string, place(p.x, p.y + 0.15, p.z, 0.5, new THREE.Vector3(0.74, 0.3, 0.74)));
      kit.add(cyl, STONE[i % 3] as string, place(p.x, p.y + 1.6, p.z, 0, new THREE.Vector3(0.5, 2.7, 0.5)));
      kit.add(box, STONE[2] as string, place(p.x, p.y + 3.0, p.z, 0.5, new THREE.Vector3(0.7, 0.22, 0.7)));
      const split = i >= L.mirrors.length;
      this.er.pieces.push(split ? this.divider(p) : this.bigMirror(p));
    });
    // The altar at the north end: a block on two steps, a pale cloth, candles; the loose stone in the wall behind it.
    const a = this.hex(L.altar.q, L.altar.r);
    kit.add(box, STONE_D[1] as string, place(a.x, a.y + 0.08, a.z + 0.15, 0, new THREE.Vector3(1.7, 0.16, 1.3)));
    kit.add(box, STONE[1] as string, place(a.x, a.y + 0.45, a.z, 0, new THREE.Vector3(1.3, 0.62, 0.8)));
    kit.add(box, STONE[2] as string, place(a.x, a.y + 0.8, a.z, 0, new THREE.Vector3(1.42, 0.1, 0.92)));
    kit.add(box, '#b6bcc5', place(a.x, a.y + 0.86, a.z + 0.05, 0, new THREE.Vector3(1.0, 0.02, 0.95)));
    for (const sx of [-0.55, 0.55]) {
      kit.add(box, '#d4d8de', place(a.x + sx, a.y + 0.98, a.z - 0.25, 0, new THREE.Vector3(0.07, 0.24, 0.07)));
      if (past || this.place === 'naveClose') this.light(new THREE.Vector3(a.x + sx, a.y + 1.14, a.z - 0.25), FLAME, 0.9, 0.85, 1);
    }
    this.er.altarAt = new THREE.Vector3(a.x, a.y + 1.0, a.z);
    const st = this.ctx.at(L.stone.x, L.stone.y, 0);
    kit.add(box, '#5b6068', place(st.x, st.y + 0.55, st.z + 0.35, 0.05, new THREE.Vector3(0.52, 0.42, 0.18)));
    kit.add(box, '#14161a', place(st.x, st.y + 0.55, st.z + 0.44, 0.05, new THREE.Vector3(0.56, 0.03, 0.02)));
    this.er.stone = new THREE.Vector3(st.x, st.y + 0.55, st.z + 0.45);
    this.er.audSlot = L.aud;
    // The nave's door at the south end: its arch, two leaves (whole in the memory, one torn off its hinge now).
    const d = this.hex(L.door.q, L.door.r);
    this.er.doorAt = new THREE.Vector3(d.x, d.y + 1.0, d.z + 0.3);
    for (const sx of [-1, 1]) kit.add(box, STONE[sx > 0 ? 0 : 2] as string, place(d.x + sx * 0.78, d.y + 1.2, d.z + 0.45, 0, new THREE.Vector3(0.32, 2.4, 0.42)));
    kit.add(box, STONE[1] as string, place(d.x, d.y + 2.45, d.z + 0.45, 0, new THREE.Vector3(1.9, 0.3, 0.44)));
    kit.add(box, '#2e2620', place(d.x - 0.32, d.y + 1.0, d.z + 0.55, 0, new THREE.Vector3(0.6, 2.0, 0.08)));
    if (past) kit.add(box, '#2e2620', place(d.x + 0.32, d.y + 1.0, d.z + 0.55, 0, new THREE.Vector3(0.6, 2.0, 0.08)));
    else kit.add(box, '#2e2620', place(d.x + 0.9, d.y + 0.35, d.z + 0.1, 0.6, new THREE.Vector3(0.6, 2.0, 0.08), 1.25, 0.2));
    // The beam over the middle and the lantern under the hole (broken now: two halves sagging; whole in the memory).
    const lc = this.hex(L.lantern.q, L.lantern.r);
    const span = 9.5;
    if (past) kit.add(box, '#3a3129', place(lc.x, lc.y + 3.3, lc.z, 0, new THREE.Vector3(span, 0.24, 0.3)));
    else
      for (const sx of [-1, 1]) {
        kit.add(box, '#3a3129', place(lc.x + sx * span * 0.3, lc.y + 3.15, lc.z, 0, new THREE.Vector3(span * 0.42, 0.24, 0.3), 0, sx * 0.12));
        kit.add(box, '#2b241f', place(lc.x + sx * 0.25, lc.y + 2.82, lc.z, 0.4, new THREE.Vector3(0.3, 0.2, 0.26), 0.4, sx * 0.6));
      }
    kit.add(box, IRON, place(lc.x, lc.y + 2.75, lc.z, 0, new THREE.Vector3(0.03, 0.8, 0.03)));
    const lan = this.lanternGroup();
    lan.position.set(lc.x, lc.y + 2.25, lc.z);
    this.group.add(lan);
    this.er.lantern = lan;
    this.er.lanternAt = lan.position.clone();
    // Under the hole: snow on the floor (now) and the shaft of pale light coming down.
    if (!past) {
      const drift = new THREE.SphereGeometry(0.5, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2);
      for (let i = 0; i < 6; i++) {
        const ang = i * 1.05 + 0.3;
        const rr = i === 0 ? 0 : 0.55 + (i % 2) * 0.2;
        kit.add(drift, SNOW[i % 3] as string, place(lc.x + Math.cos(ang) * rr, lc.y - 0.02, lc.z + Math.sin(ang) * rr * 0.8, ang, new THREE.Vector3(i === 0 ? 1.4 : 0.7, 0.12, i === 0 ? 1.1 : 0.5)));
      }
      drift.dispose();
      this.buildShaft(lc);
    }
    // The ink on the walls (now): dark runs down the stone where the limpets come out of, on the wall's face.
    if (!past)
      for (const p0 of L.ink) {
        const f = walls.face(p0.x, p0.y);
        if (!f) continue;
        const yaw = Math.atan2(f.n.x, f.n.z);
        const q = f.p.clone().addScaledVector(f.n, 0.06);
        kit.add(box, '#0c0912', place(q.x, q.y + 1.0, q.z, yaw, new THREE.Vector3(0.5, 1.7, 0.06)));
        kit.add(box, '#0c0912', place(q.x + 0.12, q.y + 0.35, q.z, yaw, new THREE.Vector3(0.16, 0.7, 0.07)));
        kit.add(box, '#0c0912', place(q.x, q.y + 0.03, q.z, yaw, new THREE.Vector3(0.7, 0.04, 0.4)));
        this.er.ink.push(new THREE.Vector3(q.x, q.y + 0.9, q.z).addScaledVector(f.n, 0.1));
      }
    // The cells' doors: dark arched doorways in the long walls, a step before each.
    for (const c of L.cells) {
      const f = walls.face(c.x, c.y);
      if (!f) continue;
      const yaw = Math.atan2(f.n.x, f.n.z);
      const q = f.p.clone().addScaledVector(f.n, 0.05);
      kit.add(box, '#0e1014', place(q.x, q.y + 0.75, q.z, yaw, new THREE.Vector3(0.66, 1.5, 0.06)));
      kit.add(box, STONE[2] as string, place(q.x, q.y + 1.56, q.z + 0.0, yaw, new THREE.Vector3(0.84, 0.14, 0.12)));
      for (const k of [-1, 1]) {
        const e = q.clone().add(new THREE.Vector3(Math.cos(yaw) * k * 0.38, 0, -Math.sin(yaw) * k * 0.38));
        kit.add(box, STONE[0] as string, place(e.x, e.y + 0.75, e.z, yaw, new THREE.Vector3(0.1, 1.5, 0.12)));
      }
      const st = q.clone().addScaledVector(f.n, 0.2);
      kit.add(box, STONE_D[2] as string, place(st.x, st.y + 0.05, st.z, yaw, new THREE.Vector3(0.8, 0.1, 0.3)));
    }
    // The memory's six lamps (the vigil's): clay lamps on iron stands, up the nave from the door to the altar.
    if (this.place === 'naveMemory' || this.place === 'navePast' || VIGIL.has(this.place)) {
      for (const lp of L.lamps) {
        const p = this.ctx.at(lp.x, lp.y, 0);
        kit.add(box, IRON, place(p.x, p.y + 0.5, p.z, 0, new THREE.Vector3(0.05, 1.0, 0.05)));
        for (let k2 = 0; k2 < 3; k2++) kit.add(box, IRON, place(p.x + Math.cos(k2 * 2.1) * 0.12, p.y + 0.08, p.z + Math.sin(k2 * 2.1) * 0.12, k2 * 2.1, new THREE.Vector3(0.24, 0.03, 0.03), 0, 0.5));
        kit.add(cyl, '#6a5446', place(p.x, p.y + 1.02, p.z, 0, new THREE.Vector3(0.3, 0.1, 0.3)));
        kit.add(cyl, '#4a3c32', place(p.x, p.y + 1.07, p.z, 0, new THREE.Vector3(0.2, 0.03, 0.2)));
        this.er.lamps.push(new THREE.Vector3(p.x, p.y + 1.16, p.z));
        this.er.lampLit.push(this.place === 'navePast' || this.place === 'naveDawn' || this.place === 'naveLit' ? 1 : 0);
        this.er.lampFlash.push(0);
      }
      this.buildRings();
    }
    if (VIGIL.has(this.place)) this.buildVigil(kit, L, s, box, cyl);
    box.dispose();
    cyl.dispose();
    this.buildNaveBeam();
    this.buildTongues();
    // The close: the way to the altar and the door lit by the sun from the start (the scene's nave set as it was won).
    if (this.place === 'naveDawn') {
      const b = naveTrace(L, L.answer.turns, L.answer.splits);
      const segs: number[] = [];
      for (let i = 0; i + 3 < b.segs.length; i += 4) {
        const a = hexToWorld(b.segs[i] as number, b.segs[i + 1] as number);
        const c = hexToWorld(b.segs[i + 2] as number, b.segs[i + 3] as number);
        segs.push(a.x, a.y, c.x, c.y);
      }
      this.rebuildNaveBeam(segs, b.weak);
      this.er.altarLit = this.er.doorLit = 1;
    }
  }

  /**
   * Chapter 5's nave: the candle on its stone by Tessa (the sim's candle, battle/vigilia-layout.ts), a crust of ice for
   * the mirror that frosts over (hidden until it does) and the brazier by it; snow drifted in under the hole.
   */
  private buildVigil(kit: Kit, L: NaveLayout, s: BattleState, box: THREE.BoxGeometry, cyl: THREE.CylinderGeometry): void {
    const a = hexToWorld(L.altar.q, L.altar.r);
    const sgn = a.x > 0 ? -1 : 1;
    const V = s.rules?.senda?.litter?.hermit?.vela;
    const c0 = V?.candle ?? { x: a.x + sgn * 500, y: a.y + 1000 };
    const c = this.ctx.at(c0.x, c0.y, 0);
    // A flat stone, the candle on it (white wax, cold-lit so the lamps' warmth never turns it cream).
    kit.add(box, STONE_D[1] as string, place(c.x, c.y + 0.09, c.z, 0.3, new THREE.Vector3(0.5, 0.18, 0.42)));
    kit.add(cyl, '#e6ecf4', place(c.x, c.y + 0.34, c.z, 0, new THREE.Vector3(0.11, 0.32, 0.11)));
    this.er.candle = new THREE.Vector3(c.x, c.y + 0.56, c.z);
    // Tessa's blanket over the altar's step: grey wool, its edge hanging.
    const al = this.hex(L.altar.q, L.altar.r);
    kit.add(box, '#5d6470', place(al.x, al.y + 0.88, al.z + 0.12, 0, new THREE.Vector3(1.14, 0.04, 0.86)));
    kit.add(box, '#4e5560', place(al.x, al.y + 0.6, al.z + 0.55, 0, new THREE.Vector3(1.0, 0.5, 0.03)));
    // The frosted mirror's crust (on its disc) and the brazier by its column.
    const M = V?.mourn;
    if (M) {
      const view = this.er.pieces[M.frost.mirror];
      if (view) {
        const ice = this.track(new THREE.MeshStandardMaterial({ color: '#dfeaf6', roughness: 0.35, metalness: 0.05, emissive: '#7f9cc4', emissiveIntensity: 0.6, transparent: true, opacity: 0.88, flatShading: true }));
        const crust = new THREE.Mesh(this.track(new THREE.IcosahedronGeometry(0.56, 1)), ice);
        crust.scale.set(1, 1, 0.32);
        crust.position.z = 0.32;
        crust.visible = false;
        view.head.add(crust);
        this.er.frost.push(crust);
        // Icicles from its rim.
        for (let i = 0; i < 5; i++) {
          const ic = new THREE.Mesh(this.track(new THREE.ConeGeometry(0.05, 0.3 + (i % 2) * 0.15, 5)), ice);
          ic.rotation.x = Math.PI;
          ic.position.set(-0.36 + i * 0.18, -0.55, 0.34);
          ic.visible = false;
          view.head.add(ic);
          this.er.frost.push(ic);
        }
      }
      const b = this.ctx.at(M.frost.brazier.x, M.frost.brazier.y, 0);
      kit.add(box, IRON, place(b.x, b.y + 0.4, b.z, 0, new THREE.Vector3(0.07, 0.8, 0.07)));
      for (let k = 0; k < 3; k++) kit.add(box, IRON, place(b.x + Math.cos(k * 2.1) * 0.16, b.y + 0.06, b.z + Math.sin(k * 2.1) * 0.16, k * 2.1, new THREE.Vector3(0.3, 0.03, 0.04), 0, 0.5));
      const bowl = new THREE.CylinderGeometry(0.34, 0.2, 0.2, 10);
      kit.add(bowl, IRON, place(b.x, b.y + 0.86, b.z, 0));
      bowl.dispose();
      kit.add(box, '#2b2622', place(b.x, b.y + 0.95, b.z, 0.4, new THREE.Vector3(0.36, 0.06, 0.36)));
      this.er.brazier = new THREE.Vector3(b.x, b.y + 1.05, b.z);
    }
    // The niche of the vigil's lamps by the altar (in the scenes, the clay lamps not yet handed out, in a row).
    const n = this.hex(L.altar.q - 2, L.altar.r + 1);
    kit.add(box, STONE[2] as string, place(n.x, n.y + 0.62, n.z, 0, new THREE.Vector3(1.4, 0.08, 0.36)));
    for (let i = 0; i < 4; i++) {
      kit.add(cyl, '#6a5446', place(n.x - 0.5 + i * 0.33, n.y + 0.72, n.z, 0, new THREE.Vector3(0.2, 0.1, 0.2)));
      kit.add(cyl, '#4a3c32', place(n.x - 0.5 + i * 0.33, n.y + 0.77, n.z, 0, new THREE.Vector3(0.12, 0.03, 0.12)));
    }
  }

  /** A great mirror on its column: an iron yoke and the steel disc (the disc turns). */
  private bigMirror(p: THREE.Vector3): PieceView {
    const g = new THREE.Group();
    g.position.copy(p);
    const iron = this.track(new THREE.MeshStandardMaterial({ color: '#33373d', roughness: 0.45, metalness: 0.45 }));
    // (Steel with no sky to show is black: a cold emissive so it reads, docs/RENDIMIENTO.md.)
    const steel = this.track(new THREE.MeshStandardMaterial({ color: '#d2dae6', roughness: 0.16, metalness: 0.3, emissive: '#6a7896', emissiveIntensity: 0.7 }));
    const head = new THREE.Group();
    head.position.y = 1.55;
    const arm = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.06, 0.06, 0.5)), iron);
    arm.position.set(0, 0, 0.0);
    head.add(arm);
    const yoke = new THREE.Mesh(this.track(new THREE.TorusGeometry(0.56, 0.04, 5, 20, Math.PI)), iron);
    yoke.rotation.z = Math.PI;
    head.add(yoke);
    const plate = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.48, 0.48, 0.06, 22)), steel);
    plate.rotation.x = Math.PI / 2;
    plate.position.z = 0.32;
    head.add(plate);
    const rim = new THREE.Mesh(this.track(new THREE.TorusGeometry(0.49, 0.035, 5, 22)), iron);
    rim.position.z = 0.32;
    head.add(rim);
    g.add(head);
    this.group.add(g);
    return { g, head, yaw: 0, want: 0, at: new THREE.Vector3(p.x, p.y + 1.55, p.z), spin: 0, split: false };
  }

  /** A divider: a pane of pale glass on its column, in an iron frame (it turns). */
  private divider(p: THREE.Vector3): PieceView {
    const g = new THREE.Group();
    g.position.copy(p);
    const iron = this.track(new THREE.MeshStandardMaterial({ color: '#33373d', roughness: 0.45, metalness: 0.45 }));
    const glass = this.track(new THREE.MeshStandardMaterial({ color: '#cfe2ff', roughness: 0.08, metalness: 0.1, emissive: '#7fa0e0', emissiveIntensity: 0.75, transparent: true, opacity: 0.7 }));
    const head = new THREE.Group();
    head.position.y = 1.55;
    const pane = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.9, 0.9, 0.05)), glass);
    pane.position.z = 0.28;
    head.add(pane);
    const frame = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.98, 0.06, 0.08)), iron);
    for (const y of [-0.47, 0.47]) {
      const f = frame.clone();
      f.position.set(0, y, 0.28);
      head.add(f);
    }
    // A cut in the glass along its middle: it parts the beam.
    const cut = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.02, 0.86, 0.06)), this.track(new THREE.MeshStandardMaterial({ color: '#ffffff', emissive: '#e8f0ff', emissiveIntensity: 1.2 })));
    cut.position.z = 0.28;
    head.add(cut);
    g.add(head);
    this.group.add(g);
    return { g, head, yaw: 0, want: 0, at: new THREE.Vector3(p.x, p.y + 1.55, p.z), spin: 0, split: true };
  }

  /** The shaft of pale light down through the hole in the roof, snow turning in it. */
  private buildShaft(lc: THREE.Vector3): void {
    this.er.shaftMat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uT: { value: 0 }, uSun: { value: 0 } },
        vertexShader: /* glsl */ `varying vec2 vUv; varying float vY; void main() { vUv = uv; vY = position.y; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
        fragmentShader: /* glsl */ `uniform float uT; uniform float uSun; varying vec2 vUv; varying float vY;
          float h(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
          void main() {
            float edge = 1.0 - abs(vUv.x - 0.5) * 2.0;
            float a = edge * edge * (0.16 + uSun * 0.3) * (0.6 + 0.4 * vUv.y);
            // Flakes falling slowly in it.
            vec2 g = vec2(vUv.x * 14.0, vUv.y * 9.0 + uT * 0.35);
            vec2 c = floor(g);
            vec2 f = fract(g) - 0.5;
            float fl = step(0.82, h(c)) * smoothstep(0.12, 0.0, length(f));
            a += fl * 0.5 * edge;
            gl_FragColor = vec4(mix(vec3(0.82, 0.88, 1.0), vec3(0.97, 0.98, 1.0), uSun) * a, a);
          }`,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      }),
    );
    const geo = this.track(new THREE.CylinderGeometry(0.9, 1.5, 6, 16, 1, true));
    const m = new THREE.Mesh(geo, this.er.shaftMat);
    m.position.set(lc.x, lc.y + 3.0, lc.z);
    m.renderOrder = 5;
    m.frustumCulled = false;
    this.group.add(m);
    this.er.shaft = m;
  }

  /** The lamps' rings of light on the floor (one instanced mesh, scaled to nothing while a lamp is dark). */
  private buildRings(): void {
    const mat = this.track(
      new THREE.ShaderMaterial({
        vertexShader: /* glsl */ `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0); }`,
        fragmentShader: /* glsl */ `varying vec2 vUv; void main() {
          float d = length(vUv - 0.5) * 2.0;
          float a = smoothstep(1.0, 0.2, d) * 0.32 + smoothstep(0.86, 0.9, d) * smoothstep(1.0, 0.92, d) * 0.6;
          gl_FragColor = vec4(vec3(1.0, 0.93, 0.86) * a, a);
        }`,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    const geo = this.track(new THREE.PlaneGeometry(1, 1));
    geo.rotateX(-Math.PI / 2);
    const m = new THREE.InstancedMesh(geo, mat, Math.max(1, this.er.lamps.length));
    m.renderOrder = 3;
    m.frustumCulled = false;
    this.er.rings = m;
    this.group.add(m);
    this.drawRings();
  }

  private drawRings(): void {
    const m = this.er.rings;
    if (!m) return;
    const o = new THREE.Object3D();
    this.er.lamps.forEach((p, i) => {
      const on = this.er.lampLit[i] ?? 0;
      const fl = this.er.lampFlash[i] ?? 0;
      o.position.set(p.x, p.y - 1.1, p.z);
      const d = on ? 4.0 + fl * 2 : 0.0001;
      o.scale.set(d, 1, d);
      o.updateMatrix();
      m.setMatrixAt(i, o.matrix);
    });
    m.instanceMatrix.needsUpdate = true;
  }

  /** The beam's strip in pieces (each with how bright it is). */
  private buildNaveBeam(): void {
    this.er.beamMat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uT: { value: 0 }, uSun: { value: 0 } },
        vertexShader: /* glsl */ `attribute float aU; attribute float aV; attribute float aW; varying float vU; varying float vV; varying float vW;
          void main() { vU = aU; vV = aV; vW = aW; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
        fragmentShader: /* glsl */ `uniform float uT; uniform float uSun; varying float vU; varying float vV; varying float vW;
          void main() {
            float x = abs(vV);
            // (The sun's beam, chapter 5: a wider white core, no blue; never gold.)
            float core = smoothstep(0.36 + uSun * 0.2, 0.12, x);
            float halo = (1.0 - x) * (1.0 - x);
            float pulse = smoothstep(0.55, 1.0, sin(vU * 1.6 - uT * 5.0));
            vec3 c = mix(mix(vec3(0.55, 0.72, 1.0), vec3(0.94, 0.96, 1.0), uSun), vec3(1.0), core * vW);
            float a = (halo * (0.7 + uSun * 0.5) + core * 1.0 + pulse * halo * 0.55) * mix(0.3, 1.0, vW);
            gl_FragColor = vec4(c * a, a);
          }`,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      }),
    );
    const g = this.track(new THREE.BufferGeometry());
    this.er.beam = new THREE.Mesh(g, this.er.beamMat);
    this.er.beam.renderOrder = 4;
    this.er.beam.frustumCulled = false;
    this.group.add(this.er.beam);
  }

  private rebuildNaveBeam(segs: number[], weak: number[]): void {
    const m = this.er.beam;
    if (!m) return;
    const pos: number[] = [];
    const u: number[] = [];
    const v: number[] = [];
    const wv: number[] = [];
    const idx: number[] = [];
    // (Wide and bright: the nave is seen whole from high up, the beam has to be followed across it on a phone.)
    const half = 0.8;
    // (At the nave's floor all along: a piece's ends are columns, the altar, the door, which the terrain stands up as
    // rock; at their height the beam sank into it or floated, and only two of seven pieces showed. docs/RENDIMIENTO.md.)
    const L = this.er.L;
    const fy = L ? this.hex(L.lantern.q, L.lantern.r, 0.09).y : 0.09;
    for (let i = 0; i + 3 < segs.length; i += 4) {
      const a = this.ctx.at(segs[i] as number, segs[i + 1] as number, 0.09).setY(fy);
      const b = this.ctx.at(segs[i + 2] as number, segs[i + 3] as number, 0.09).setY(fy);
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const len = Math.hypot(dx, dz) || 1;
      const nx = (-dz / len) * half;
      const nz = (dx / len) * half;
      const k = pos.length / 3;
      pos.push(a.x + nx, a.y, a.z + nz, a.x - nx, a.y, a.z - nz, b.x + nx, b.y, b.z + nz, b.x - nx, b.y, b.z - nz);
      u.push(0, 0, len, len);
      v.push(1, -1, 1, -1);
      const w = (weak[i / 4] ?? 0) >= 2 ? 0 : 1;
      wv.push(w, w, w, w);
      idx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
    }
    const g = m.geometry;
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('aU', new THREE.Float32BufferAttribute(u, 1));
    g.setAttribute('aV', new THREE.Float32BufferAttribute(v, 1));
    g.setAttribute('aW', new THREE.Float32BufferAttribute(wv, 1));
    g.setIndex(idx);
    g.computeBoundingSphere();
  }

  /** The Doliente's tongues: dark ribbons on the floor from her to each tip (normal blending: ink on pale stone). */
  private buildTongues(): void {
    this.er.tongueMat = this.track(
      new THREE.ShaderMaterial({
        uniforms: { uT: { value: 0 } },
        vertexShader: /* glsl */ `attribute float aU; attribute float aV; varying float vU; varying float vV;
          void main() { vU = aU; vV = aV; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
        fragmentShader: /* glsl */ `uniform float uT; varying float vU; varying float vV;
          void main() {
            float x = abs(vV);
            float body = smoothstep(1.0, 0.55, x + 0.12 * sin(vU * 6.0 - uT * 4.0));
            float rim = smoothstep(0.55, 0.8, x) * smoothstep(1.0, 0.8, x);
            vec3 c = mix(vec3(0.04, 0.03, 0.06), vec3(0.42, 0.29, 0.66), rim);
            gl_FragColor = vec4(c, body * 0.9);
          }`,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    // (Fixed buffers, written in place: rebuilt with new attributes at every tick while they crawled, the Doliente
    // cost a phone 132 ms/s, docs/RENDIMIENTO.md. Room for TONGUES tongues of TONGUE_N pieces; the draw range shows
    // those there are.)
    const g = this.track(new THREE.BufferGeometry());
    const verts = TONGUES * (TONGUE_N + 1) * 2;
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(verts * 3), 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aU', new THREE.BufferAttribute(new Float32Array(verts), 1).setUsage(THREE.DynamicDrawUsage));
    const vv = new Float32Array(verts);
    for (let i = 0; i < verts; i++) vv[i] = i % 2 === 0 ? 1 : -1;
    g.setAttribute('aV', new THREE.BufferAttribute(vv, 1));
    const idx: number[] = [];
    for (let t = 0; t < TONGUES; t++)
      for (let i = 0; i < TONGUE_N; i++) {
        const k = t * (TONGUE_N + 1) * 2 + i * 2;
        idx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
      }
    g.setIndex(idx);
    g.setDrawRange(0, 0);
    this.er.tongues = new THREE.Mesh(g, this.er.tongueMat);
    this.er.tongues.renderOrder = 4;
    this.er.tongues.frustumCulled = false;
    this.group.add(this.er.tongues);
  }

  private rebuildTongues(s: BattleState): void {
    const m = this.er.tongues;
    const erm = s.senda?.litter?.erm;
    if (!m || !erm) return;
    const boss = s.enemies.find((e) => e.id === erm.boss && e.hp > 0);
    const g = m.geometry;
    const pa = g.getAttribute('position') as THREE.BufferAttribute;
    const ua = g.getAttribute('aU') as THREE.BufferAttribute;
    const pos = pa.array as Float32Array;
    const u = ua.array as Float32Array;
    let nt = 0;
    if (boss)
      for (const t of erm.tongues) {
        if (nt >= TONGUES) break;
        const e = s.enemies.find((x) => x.id === t.id && x.hp > 0);
        if (!e) continue;
        const a = this.ctx.at(boss.x, boss.y, 0.05);
        const b = this.ctx.at(e.x, e.y, 0.05).setY(a.y);
        // A gentle curve from her hem to the tip, in six pieces, narrowing.
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const len = Math.hypot(dx, dz) || 1;
        const nx = -dz / len;
        const nz = dx / len;
        const n = TONGUE_N;
        let k = nt * (n + 1) * 2;
        for (let i = 0; i <= n; i++) {
          const f = i / n;
          const bend = Math.sin(f * Math.PI) * 0.5 * Math.sin(t.id * 1.7);
          const cx = a.x + dx * f + nx * bend;
          const cz = a.z + dz * f + nz * bend;
          const w = 0.42 * (1 - f) + 0.16;
          pos[k * 3] = cx + nx * w;
          pos[k * 3 + 1] = a.y;
          pos[k * 3 + 2] = cz + nz * w;
          pos[k * 3 + 3] = cx - nx * w;
          pos[k * 3 + 4] = a.y;
          pos[k * 3 + 5] = cz - nz * w;
          u[k] = u[k + 1] = f * len;
          k += 2;
        }
        nt++;
      }
    pa.needsUpdate = true;
    ua.needsUpdate = true;
    g.setDrawRange(0, nt * TONGUE_N * 6);
  }

  /* ---------- the refectory, the cliff ---------- */

  private buildRefectory(kit: Kit): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    // The walls round it, carved stone with niches; the hearth on the north wall.
    for (const h of this.map.rocks ?? []) {
      const p = this.hex(h.q, h.r);
      const tall = h.r <= -4 ? 3.0 : h.r >= 4 ? 1.0 : 2.2;
      kit.add(box, STONE[Math.floor(this.k(h.q, h.r, 2) * 2.99)] as string, place(p.x, p.y + tall / 2, p.z, 0, new THREE.Vector3(1.05, tall, 1.05)));
    }
    // The long table and its benches, dark wood; bowls, a jug, bread.
    const t = this.hex(0, -2);
    kit.add(box, '#3a3129', place(t.x, t.y + 0.75, t.z, 0, new THREE.Vector3(3.6, 0.1, 0.8)));
    for (const sx of [-1.6, 1.6]) kit.add(box, '#2b241f', place(t.x + sx, t.y + 0.36, t.z, 0, new THREE.Vector3(0.12, 0.72, 0.6)));
    for (const sz of [-0.7, 0.7]) kit.add(box, '#2e2721', place(t.x, t.y + 0.42, t.z + sz, 0, new THREE.Vector3(3.4, 0.08, 0.3)));
    for (let i = 0; i < 5; i++) kit.add(box, i % 2 ? '#8a8072' : '#6a6258', place(t.x - 1.4 + i * 0.7, t.y + 0.85, t.z + (i % 2 ? 0.15 : -0.15), i, new THREE.Vector3(0.18, 0.08, 0.18)));
    kit.add(box, '#e0e4ea', place(t.x + 1.2, t.y + 0.92, t.z - 0.2, 0, new THREE.Vector3(0.06, 0.22, 0.06)));
    this.light(new THREE.Vector3(t.x + 1.2, t.y + 1.08, t.z - 0.2), FLAME, 0.9, 0.85, 1);
    box.dispose();
    this.er.hearth = this.hex(0, 0, 0.3);
  }

  private buildCliff(kit: Kit, far: boolean): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    // The cliff: the rock cells to the north stand tall and pale, stepping up; the Hermitage cut in its face.
    for (const h of this.map.rocks ?? []) {
      const p = this.hex(h.q, h.r);
      const k = this.k(h.q, h.r, 9);
      if (h.r <= -3) {
        const tall = 4.5 + (-3 - h.r) * 1.6 + k * 1.2;
        kit.add(box, STONE_D[Math.floor(k * 2.99)] as string, place(p.x, p.y + tall / 2, p.z, k, new THREE.Vector3(1.15, tall, 1.15)));
        if (k > 0.6) kit.add(box, SNOW[Math.floor(k * 2.99)] as string, place(p.x, p.y + tall + 0.05, p.z, k, new THREE.Vector3(1.0, 0.12, 1.0)));
      } else kit.add(box, SNOW[Math.floor(k * 2.99)] as string, place(p.x, p.y + 0.4, p.z, k, new THREE.Vector3(1.1, 0.8 + k * 0.6, 1.1)));
    }
    // The facade cut in the cliff: three broad steps, a face of pale cut stone in courses between two buttresses, the
    // door in its arch (broken: a leaf torn off, lying on the steps), two lancet windows, a carved cornice, the bell
    // gable over it with its empty arch; ink running down from the rose window.
    const f = this.hex(0, -3);
    const fz = f.z + 0.6;
    for (let i = 0; i < 3; i++) kit.add(box, STONE[i % 3] as string, place(f.x, f.y + 0.1 + i * 0.2, fz + 1.0 - i * 0.3, 0, new THREE.Vector3(3.4 - i * 0.5, 0.2, 0.6)));
    for (let i = 0; i < 6; i++) kit.add(box, STONE[(i + 1) % 3] as string, place(f.x, f.y + 0.5 + i, fz, 0, new THREE.Vector3(4.4 - (i % 2) * 0.06, 1.0, 0.4)));
    for (let i = 0; i < 6; i++) kit.add(box, STONE_D[0] as string, place(f.x, f.y + i + 1.0, fz + 0.21, 0, new THREE.Vector3(4.42, 0.04, 0.02)));
    for (const sx of [-1, 1]) {
      kit.add(box, STONE[2] as string, place(f.x + sx * 2.35, f.y + 2.8, fz + 0.25, 0, new THREE.Vector3(0.5, 5.6, 0.7)));
      kit.add(box, STONE[0] as string, place(f.x + sx * 2.35, f.y + 5.8, fz + 0.2, 0, new THREE.Vector3(0.4, 0.5, 0.5), 0, sx * 0.4));
      kit.add(box, SNOW[0] as string, place(f.x + sx * 2.35, f.y + 5.62, fz + 0.25, 0, new THREE.Vector3(0.52, 0.06, 0.72)));
      // A lancet window each side: dark, its stone frame.
      kit.add(box, '#101216', place(f.x + sx * 1.35, f.y + 3.0, fz + 0.21, 0, new THREE.Vector3(0.34, 1.2, 0.04)));
      kit.add(box, STONE[2] as string, place(f.x + sx * 1.35, f.y + 3.7, fz + 0.22, 0, new THREE.Vector3(0.5, 0.14, 0.06)));
    }
    // The door's arch: jambs, a half ring of voussoirs, the dark inside.
    kit.add(box, '#0f1014', place(f.x, f.y + 1.45, fz + 0.21, 0, new THREE.Vector3(1.2, 2.3, 0.04)));
    for (const sx of [-1, 1]) kit.add(box, STONE[0] as string, place(f.x + sx * 0.72, f.y + 1.35, fz + 0.28, 0, new THREE.Vector3(0.26, 2.1, 0.2)));
    const arch = new THREE.TorusGeometry(0.72, 0.14, 5, 12, Math.PI);
    kit.add(arch, STONE[2] as string, place(f.x, f.y + 2.4, fz + 0.28, 0));
    arch.dispose();
    kit.add(box, '#2e2620', place(f.x + 0.55, f.y + 0.55, fz + 0.95, 0.5, new THREE.Vector3(0.62, 0.08, 1.9), 0, 0.18));
    for (const z of [0.4, 1.3]) kit.add(box, IRON, place(f.x + 0.55, f.y + 0.61, fz + 0.95 - 0.95 + z, 0.5, new THREE.Vector3(0.58, 0.02, 0.05), 0, 0.18));
    // The rose window, dark; ink running down the face from it and from the door.
    const rose = new THREE.TorusGeometry(0.55, 0.12, 6, 16);
    kit.add(rose, STONE[0] as string, place(f.x, f.y + 4.4, fz + 0.24, 0));
    rose.dispose();
    kit.add(box, '#14161b', place(f.x, f.y + 4.4, fz + 0.22, 0, new THREE.Vector3(0.8, 0.8, 0.04)));
    for (const sx of [-0.3, 0.25, -0.95, 0.95]) kit.add(box, '#0c0912', place(f.x + sx, f.y + 3.3 - Math.abs(sx) * 1.1, fz + 0.23, 0, new THREE.Vector3(0.12, 1.8 + sx * 0.4, 0.03)));
    this.er.ink.push(new THREE.Vector3(f.x - 0.3, f.y + 3.0, fz + 0.3), new THREE.Vector3(f.x + 0.95, f.y + 2.2, fz + 0.3));
    // The cornice and the bell gable, its empty arch; snow on every ledge.
    kit.add(box, STONE[2] as string, place(f.x, f.y + 6.05, fz + 0.1, 0, new THREE.Vector3(4.8, 0.2, 0.7)));
    kit.add(box, SNOW[1] as string, place(f.x, f.y + 6.18, fz + 0.1, 0, new THREE.Vector3(4.7, 0.07, 0.68)));
    kit.add(box, STONE[1] as string, place(f.x, f.y + 6.9, fz, 0, new THREE.Vector3(1.3, 1.5, 0.4)));
    kit.add(box, '#0f1014', place(f.x, f.y + 6.95, fz + 0.21, 0, new THREE.Vector3(0.5, 0.8, 0.04)));
    kit.add(box, SNOW[0] as string, place(f.x, f.y + 7.68, fz, 0, new THREE.Vector3(1.36, 0.08, 0.44)));
    // The broken roof of the nave behind: slate slabs tipped in, the gap between them.
    for (const sx of [-1, 1]) kit.add(box, '#353b44', place(f.x + sx * 1.3, f.y + 6.5, f.z - 0.9, 0, new THREE.Vector3(2.0, 0.14, 2.2), 0.3 * sx, sx * 0.4));
    // The tower out over nothing on the right, the great lantern dark at its top.
    const tw = this.hex(4, -4);
    kit.add(box, STONE[1] as string, place(tw.x + 1.4, tw.y + 5.5, tw.z, 0, new THREE.Vector3(1.4, 11, 1.4)));
    kit.add(box, STONE[2] as string, place(tw.x + 1.4, tw.y + 11.2, tw.z, 0, new THREE.Vector3(1.8, 0.3, 1.8)));
    kit.add(box, IRON, place(tw.x + 1.4, tw.y + 12.0, tw.z, Math.PI / 4, new THREE.Vector3(0.9, 1.3, 0.9)));
    kit.add(box, '#2c3340', place(tw.x + 1.4, tw.y + 12.0, tw.z, Math.PI / 4, new THREE.Vector3(0.7, 1.05, 0.7)));
    kit.add(box, IRON, place(tw.x + 1.4, tw.y + 12.8, tw.z, Math.PI / 4, new THREE.Vector3(0.5, 0.3, 0.5)));
    // The way up: trodden snow; a brazier lit by the door (Piet's, with the stretcher).
    const bz = this.hex(-2, 0);
    kit.add(box, IRON, place(bz.x, bz.y + 0.45, bz.z, 0, new THREE.Vector3(0.08, 0.9, 0.08)));
    const bowl = new THREE.CylinderGeometry(0.34, 0.22, 0.2, 10);
    kit.add(bowl, IRON, place(bz.x, bz.y + 0.92, bz.z, 0));
    bowl.dispose();
    this.light(new THREE.Vector3(bz.x, bz.y + 1.15, bz.z), new THREE.Color('#ff8a5a'), 2.2, 0.9, 1);
    this.light(new THREE.Vector3(bz.x, bz.y + 1.05, bz.z), FLAME, 0.9, 0.9, 1);
    if (far) this.light(new THREE.Vector3(tw.x + 1.4, tw.y + 12.0, tw.z), RED, 0.8, 0.0, 0);
    box.dispose();
  }

  /**
   * The atrium (chapter 5, act 2): its walls carved like the nave's (tall to the north, low to the south: the camera looks
   * in from the cliff's door), the four great pillars, the cells' arches, niches with candle stubs, the pilgrims' bones;
   * the nave's door shut and barred with planks; the cliff's door open on the snow.
   */
  private buildAtrium(kit: Kit): void {
    const A = atriumLayout(1);
    this.er.atrium = A;
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    const ball = new THREE.SphereGeometry(0.5, 7, 5);
    const R = A.radius;
    const rocks = new Set(A.rocks.map((h) => key(h.q, h.r)));
    const pil = new Set(A.pillars.map((h) => key(h.q, h.r)));
    const floor = new Set<string>();
    for (const h of allCells(R)) if (!rocks.has(key(h.q, h.r))) floor.add(key(h.q, h.r));
    const D: [number, number][] = [
      [1, 0],
      [1, -1],
      [0, -1],
      [-1, 0],
      [-1, 1],
      [0, 1],
    ];
    let n = 0;
    for (const h of A.rocks) {
      if (pil.has(key(h.q, h.r))) continue;
      const by = D.some(([dq, dr]) => floor.has(key(h.q + dq, h.r + dr)));
      const p = this.hex(h.q, h.r);
      const k = this.k(h.q, h.r, 5);
      if (!by) {
        kit.add(box, '#23262c', place(p.x, p.y + 0.25, p.z, 0, new THREE.Vector3(1.16, 0.5, 1.16)));
        continue;
      }
      const tall = h.r <= -6 ? 3.2 : h.r >= 6 ? 0.9 : 2.4 - (h.r + 6) * 0.08;
      kit.add(box, STONE_D[2] as string, place(p.x, p.y + 0.15, p.z, 0, new THREE.Vector3(1.12, 0.3, 1.12)));
      kit.add(box, STONE[Math.floor(k * 2.99)] as string, place(p.x, p.y + tall / 2 + 0.1, p.z, 0, new THREE.Vector3(1.08, tall - 0.2, 1.08)));
      for (let i = 1; i < 4; i++) kit.add(box, STONE_D[1] as string, place(p.x, p.y + (tall * i) / 4, p.z, 0, new THREE.Vector3(1.1, 0.035, 1.1)));
      kit.add(box, STONE[2] as string, place(p.x, p.y + tall + 0.06, p.z, 0, new THREE.Vector3(1.18, 0.14, 1.18)));
      const toward = D.find(([dq, dr]) => floor.has(key(h.q + dq, h.r + dr)));
      if (!toward) continue;
      const f = this.hex(h.q + toward[0], h.r + toward[1]);
      const yaw = Math.atan2(f.x - p.x, f.z - p.z);
      const nx = p.x + Math.sin(yaw) * 0.5;
      const nz = p.z + Math.cos(yaw) * 0.5;
      const sel = n++ % 5;
      if (sel === 0 && tall > 1.4) {
        // A niche, its candle stub lit (the Hermitage kept them, even now).
        kit.add(box, '#121418', place(nx, p.y + 1.15, nz, yaw, new THREE.Vector3(0.46, 0.7, 0.06)));
        kit.add(box, STONE[2] as string, place(nx, p.y + 1.55, nz, yaw, new THREE.Vector3(0.6, 0.1, 0.12)));
        kit.add(box, '#d4d8de', place(nx, p.y + 0.9, nz, yaw, new THREE.Vector3(0.06, 0.16, 0.06)));
        this.light(new THREE.Vector3(nx, p.y + 1.04, nz), FLAME, 0.7, 0.6, 1);
      } else if (sel === 2) {
        // A pilgrim's bones by the wall: a skull, ribs, a staff.
        const bx = p.x + Math.sin(yaw) * 0.85;
        const bz = p.z + Math.cos(yaw) * 0.85;
        kit.add(ball, '#cfc9be', place(bx, p.y + 0.1, bz, yaw, new THREE.Vector3(0.22, 0.2, 0.24)));
        for (let i = 0; i < 4; i++) kit.add(box, '#bdb7ac', place(bx + Math.sin(yaw + i) * 0.2, p.y + 0.04, bz + Math.cos(yaw + i) * 0.2, yaw + i * 0.8, new THREE.Vector3(0.36, 0.04, 0.05)));
        kit.add(box, '#4a3e34', place(bx - 0.2, p.y + 0.03, bz + 0.1, yaw + 0.6, new THREE.Vector3(1.1, 0.04, 0.05)));
      } else if (sel === 4 && tall > 1.2) {
        // Ink down the stone.
        kit.add(box, '#0c0912', place(nx, p.y + 0.8, nz, yaw, new THREE.Vector3(0.3, 1.4, 0.06)));
        this.er.ink.push(new THREE.Vector3(nx, p.y + 0.7, nz));
      }
    }
    // The four great pillars: a plinth, a fluted shaft, a capital.
    A.pillars.forEach((h, i) => {
      const p = this.hex(h.q, h.r);
      kit.add(box, STONE_D[0] as string, place(p.x, p.y + 0.18, p.z, 0.5, new THREE.Vector3(0.95, 0.36, 0.95)));
      kit.add(cyl, STONE[i % 3] as string, place(p.x, p.y + 1.7, p.z, 0, new THREE.Vector3(0.66, 2.8, 0.66)));
      for (let k = 0; k < 6; k++) kit.add(box, STONE_D[1] as string, place(p.x + Math.cos(k * 1.05) * 0.33, p.y + 1.7, p.z + Math.sin(k * 1.05) * 0.33, k * 1.05, new THREE.Vector3(0.04, 2.7, 0.06)));
      kit.add(box, STONE[2] as string, place(p.x, p.y + 3.2, p.z, 0.5, new THREE.Vector3(0.9, 0.24, 0.9)));
    });
    // The cells' arches in the long walls.
    for (const c of A.cells) {
      const p = this.ctx.at(c.x + (c.x > 0 ? 500 : -500), c.y, 0);
      kit.add(box, '#0f1014', place(p.x, p.y + 0.85, p.z, Math.PI / 2, new THREE.Vector3(0.9, 1.7, 0.08)));
      kit.add(box, STONE[2] as string, place(p.x, p.y + 1.78, p.z, Math.PI / 2, new THREE.Vector3(1.1, 0.16, 0.16)));
    }
    // The nave's door, shut: two leaves of dark oak, iron straps, the planks Piet barred it with (they shake when struck).
    const d = this.hex(A.doorHex.q, A.doorHex.r);
    for (const sx of [-1, 1]) kit.add(box, STONE[sx > 0 ? 0 : 2] as string, place(d.x + sx * 0.78, d.y + 1.25, d.z - 0.4, 0, new THREE.Vector3(0.34, 2.5, 0.44)));
    kit.add(box, STONE[1] as string, place(d.x, d.y + 2.55, d.z - 0.4, 0, new THREE.Vector3(1.95, 0.32, 0.46)));
    kit.add(box, '#2e2620', place(d.x, d.y + 1.05, d.z - 0.3, 0, new THREE.Vector3(1.24, 2.1, 0.1)));
    for (const y of [0.45, 1.05, 1.65]) kit.add(box, IRON, place(d.x, d.y + y, d.z - 0.24, 0, new THREE.Vector3(1.26, 0.05, 0.03)));
    const bars = new THREE.Group();
    bars.position.set(d.x, d.y, d.z - 0.15);
    const wood = this.track(new THREE.MeshStandardMaterial({ color: '#4a3c30', roughness: 0.9, flatShading: true }));
    for (const [y, rz] of [
      [0.7, 0.32],
      [1.35, -0.28],
    ] as const) {
      const plank = new THREE.Mesh(this.track(new THREE.BoxGeometry(1.7, 0.16, 0.08)), wood);
      plank.position.set(0, y, 0);
      plank.rotation.z = rz;
      bars.add(plank);
    }
    this.group.add(bars);
    this.er.bars = bars;
    this.er.doorAt = new THREE.Vector3(d.x, d.y + 1.0, d.z - 0.2);
    // The cliff's door at the south end, open on the night: snow drifted in over the sill.
    const g = this.hex(A.gateHex.q, A.gateHex.r);
    const drift = new THREE.SphereGeometry(0.5, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2);
    for (let i = 0; i < 5; i++) kit.add(drift, SNOW[i % 3] as string, place(g.x - 0.8 + i * 0.4, g.y - 0.02, g.z + 0.3 + (i % 2) * 0.3, i, new THREE.Vector3(0.8, 0.12, 0.55)));
    drift.dispose();
    for (const sx of [-1, 1]) kit.add(box, STONE[1] as string, place(g.x + sx * 0.8, g.y + 0.5, g.z + 0.6, 0, new THREE.Vector3(0.34, 1.0, 0.4)));
    box.dispose();
    cyl.dispose();
    ball.dispose();
  }

  /** The epilogue's grave at the foot of the tower: a mound of earth and snow, an uncut stone, the lamps round it. */
  private buildGrave(kit: Kit): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const g = this.hex(2, -2);
    const mound = new THREE.SphereGeometry(0.5, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    kit.add(mound, '#4a4440', place(g.x, g.y - 0.02, g.z, 0.3, new THREE.Vector3(1.0, 0.3, 1.7)));
    kit.add(mound, SNOW[1] as string, place(g.x + 0.1, g.y + 0.06, g.z - 0.2, 0.3, new THREE.Vector3(0.6, 0.18, 0.9)));
    mound.dispose();
    // The stone, uncut: a slab of the cliff's own rock stood on end.
    const st = new THREE.DodecahedronGeometry(0.5, 0);
    kit.add(st, STONE_D[0] as string, place(g.x, g.y + 0.55, g.z - 0.95, 0.4, new THREE.Vector3(0.55, 1.1, 0.3)));
    st.dispose();
    // The vigil's lamps set round it, out with the day: clay, small.
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 10);
    for (let i = 0; i < 6; i++) {
      const a = -0.3 + (i / 5) * (Math.PI + 0.6);
      const x = g.x + Math.cos(a) * 1.25;
      const z = g.z + Math.sin(a) * 1.6;
      kit.add(cyl, '#6a5446', place(x, g.y + 0.06, z, 0, new THREE.Vector3(0.2, 0.1, 0.2)));
      kit.add(cyl, '#4a3c32', place(x, g.y + 0.11, z, 0, new THREE.Vector3(0.12, 0.03, 0.12)));
      this.er.grave.push(new THREE.Vector3(x, g.y + 0.18, z));
    }
    cyl.dispose();
    box.dispose();
  }

  /* ---------- the light ---------- */

  override apply(terrain: TerrainBuild, hour: Hour = 'night'): void {
    super.apply(terrain, hour);
    if (this.place === 'ermitaTomb') {
      // The epilogue's snow a shade down and colder: at the morning's full light it burnt white round the grave.
      const cc = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
      if (cc) {
        for (let i = 0; i < cc.count; i++) cc.setXYZ(i, cc.getX(i) * 0.6, cc.getY(i) * 0.63, cc.getZ(i) * 0.72);
        cc.needsUpdate = true;
      }
      this.paint(hour);
    }
    if (!this.inside) return;
    // The floor: pale flagstones, worn smooth down the middle (CaminoLayer's inside is floorboards).
    const geo = terrain.mesh.geometry;
    const cc = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pp = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (cc && pp) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      const c = new THREE.Color();
      for (let i = 0; i < cc.count; i++) {
        v.fromBufferAttribute(pp, i).applyMatrix4(terrain.mesh.matrixWorld);
        const n = 0.5 + 0.5 * Math.sin(v.x * 2.3 + v.z * 0.7) * Math.cos(v.z * 1.9 - v.x * 0.4);
        // (No joint lines: on the terrain's coarse vertices one ran as a dark streak down the nave's axis.)
        const mid = Math.max(0, 1 - Math.abs(v.x) / 3.5);
        c.set(FLAG[Math.floor(n * 2.99)] as string).multiplyScalar(0.66 + mid * 0.16 + n * 0.08);
        cc.setXYZ(i, c.r, c.g, c.b);
      }
      cc.needsUpdate = true;
    }
    this.paint(hour);
  }

  override paint(hour: Hour): void {
    super.paint(hour);
    if (this.place === 'ermitaTomb') {
      // The epilogue (the grave in the morning) was washed out white: the street's dawn on snow from every side. A low
      // sun now, the sky's light halved, so the stone, the figures and the lamps hold their shapes.
      const { hemi, key: kl, rim } = this.ctx;
      if (hemi) {
        hemi.color.set('#aab6cc');
        hemi.groundColor.set('#1c1f26');
        hemi.intensity = 0.72;
      }
      if (kl) {
        kl.color.set('#ffe2d2');
        kl.intensity = 1.2;
      }
      if (rim) {
        rim.color.set('#9fb4e6');
        rim.intensity = 0.65;
      }
      const scene = this.ctx.scene;
      scene.background = new THREE.Color('#4e5c76');
      if (scene.fog instanceof THREE.Fog) scene.fog.color.set('#56637c');
      return;
    }
    if (!this.inside) return;
    // Inside the rock: not black (docs/sendas: «nada de escenas casi negras»). Cold blue now, under the hole; candle
    // white in the memory and the refectory (the memory is drawn grey by the screen anyway).
    const warm = this.er.past || this.place === 'naveDawn';
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(warm ? '#100f12' : '#0c1019');
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(warm ? '#141216' : '#0f1420');
      scene.fog.near = 22;
      scene.fog.far = 70;
    }
    const { hemi, key: kl, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(warm ? '#c8c0bc' : '#8f9ec4');
      hemi.groundColor.set('#15161b');
      hemi.intensity = warm ? 1.1 : 1.2;
    }
    if (kl) {
      kl.color.set(warm ? '#efe2dc' : '#c4d2f4');
      kl.intensity = warm ? 0.8 : 0.85;
    }
    if (rim) {
      rim.color.set(warm ? '#ffb8a0' : '#8fa8ff');
      rim.intensity = 0.8;
    }
    void HOURS;
  }

  override takeLight(f: THREE.PointLight): void {
    super.takeLight(f);
    if (this.place === 'ermitaTomb') {
      // (The morning: no lantern. Left on, the board's warm light at 9 by the figures burnt the epilogue white. The
      // world adds its flicker to any base, and hiding a light recompiles every shader: kept, out of reach, far below.)
      f.intensity = this.lightBase = 0;
      f.distance = 0.01;
      f.position.set(0, -50, 0);
      return;
    }
    if (this.er.lanternAt && !this.er.past) {
      f.color.set('#e8eeff');
      f.position.copy(this.er.lanternAt);
      f.distance = 9;
      f.intensity = this.lightBase = 6;
    } else if (this.er.altarAt) {
      // The memory: the altar's candles.
      f.color.set('#f6e8e0');
      f.position.copy(this.er.altarAt).setY(this.er.altarAt.y + 0.8);
      f.distance = 11;
      f.intensity = this.lightBase = 7;
    } else if (this.er.atrium && this.er.doorAt) {
      // The atrium: a cold white over its middle, from the niches' candles and the snow's glare at the door.
      const c = this.hex(0, 0, 0);
      f.color.set('#e4ecff');
      f.position.set(c.x, c.y + 3.6, c.z);
      f.distance = 14;
      f.intensity = this.lightBase = 6;
    } else if (this.er.hearth) {
      // (A cool white over the stone: under a strong warm light the pale stone went beige and pink.)
      f.color.set('#f2e4dc');
      f.position.copy(this.er.hearth).setY(this.er.hearth.y + 1.4);
      f.distance = 9;
      f.intensity = this.lightBase = 4;
    }
  }

  /* ---------- each tick, draw and frame ---------- */

  override tick(s: BattleState): void {
    super.tick(s);
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.2);
      if (e.kind === 'mirror') {
        const v = this.er.pieces[e.n ?? 0];
        if (v) {
          v.spin = 1;
          this.ctx.vfx.burst(v.at, { n: 10, color: '#e8f0ff', speed: 1.6, up: 0.6, size: 0.08, life: 8 });
        }
        this.er.beamKey = '';
      } else if (e.kind === 'kindle') {
        const i = e.n ?? 0;
        this.er.lampLit[i] = 1;
        this.er.lampFlash[i] = 1;
        const l = this.er.lamps[i];
        if (l) {
          this.ctx.flare(l, '#ffffff', 2.4, 12);
          this.ctx.vfx.ring(l.clone().setY(l.y - 1.1), 0.3, 2.4, '#fff2e6', 12, 0.9);
        }
      } else if (e.kind === 'snuffed') {
        const i = e.n ?? 0;
        // (Chapter 5: the candle out, n 6.)
        if (i === 6 && this.er.candle) {
          this.er.candleLit = 0;
          this.ctx.vfx.burst(this.er.candle, { n: 18, color: '#1a1226', speed: 0.6, up: 1.4, size: 0.12, life: 18, smoke: true });
        }
        this.er.lampLit[i] = 0;
        const l = this.er.lamps[i];
        if (l) this.ctx.vfx.burst(l, { n: 14, color: '#1a1226', speed: 1.0, up: 1.2, size: 0.14, life: 14, smoke: true });
      } else if (e.kind === 'driven') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.1), 0.4, 3.6, '#f4f8ff', 14, 0.9);
      } else if (e.kind === 'wailWarn') {
        this.er.wailWarn = 1;
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.1), 0.5, 3.0, '#6a4aa8', 30, 0.7);
      } else if (e.kind === 'wail') {
        this.er.wail = 1;
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.1), 0.6, 7.0, '#3a2a5a', 40, 0.9);
        this.ctx.shake(0.25);
      } else if (e.kind === 'hush') {
        this.er.wail = 0;
        this.ctx.flare(p.clone().setY(p.y + 1.6), '#ffffff', 3.0, 12);
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.1), 0.5, 4.0, '#f4f8ff', 14, 0.9);
      } else if (e.kind === 'veil') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.8), { n: 10, color: '#120c1c', speed: 1.6, up: 0.6, size: 0.16, life: 10 });
      } else if (e.kind === 'tongue') {
        this.ctx.vfx.burst(p, { n: 8, color: '#120c1c', speed: 0.8, up: 0.4, size: 0.14, life: 10 });
      } else if (e.kind === 'take' && this.er.stone) {
        this.ctx.flare(this.er.stone, '#e8f0ff', 2.0, 12);
        this.ctx.vfx.burst(this.er.stone, { n: 16, color: '#8a8e96', speed: 1.2, up: 0.8, size: 0.1, life: 12, smoke: true });
      } else if (e.kind === 'set') {
        const v = this.er.pieces[e.n ?? -1];
        if (v) {
          v.g.visible = true;
          this.ctx.flare(v.at, '#ffffff', 2.6, 14);
          this.ctx.vfx.ring(v.at.clone().setY(v.at.y - 1.5), 0.3, 2.2, '#e8f0ff', 10, 0.9);
        }
        this.er.beamKey = '';
      } else if (e.kind === 'lamp' && this.er.altarAt && this.er.doorAt) {
        // Both lit: the altar and the door flash, the nave shakes a little.
        this.er.both = 1;
        for (const at of [this.er.altarAt, this.er.doorAt]) {
          this.ctx.flare(at, '#ffffff', 4.4, 18);
          this.ctx.vfx.ring(at.clone().setY(at.y - 0.95), 0.4, 4.5, '#f4f8ff', 16, 0.9);
          this.ctx.vfx.burst(at, { n: 26, color: '#ffffff', speed: 2.4, up: 1.4, size: 0.09, life: 14 });
        }
        this.ctx.shake(0.35);
      } else if (e.kind === 'dirgeWarn') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.1), 0.3, 2.0, '#e6e2da', 24, 0.8);
      } else if (e.kind === 'dirge') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 2.4), { n: 22, color: '#d8d2c6', speed: 2.0, up: -1.2, size: 0.1, life: 14, gravity: 3 });
        this.ctx.shake(0.2);
      } else if (e.kind === 'sun') {
        if ((e.n ?? 0) > 0) {
          this.er.sun = 1;
          if (this.er.lanternAt) this.ctx.flare(this.er.lanternAt.clone().setY(this.er.lanternAt.y + 1.5), '#ffffff', 7, 30);
          this.ctx.shake(0.3);
        }
      } else if (e.kind === 'frost') {
        const v = this.er.pieces[e.n ?? -1];
        if (v) this.ctx.vfx.burst(v.at, { n: 22, color: '#dfeaf6', speed: 1.4, up: 0.6, size: 0.08, life: 16 });
      } else if (e.kind === 'thaw' && this.er.brazier) {
        this.er.brazierLit = 1;
        this.ctx.flare(this.er.brazier, '#ff8a6e', 2.6, 14);
        const v = this.er.pieces[e.n ?? -1];
        if (v) this.ctx.vfx.burst(v.at, { n: 16, color: '#cfe2ff', speed: 1.0, up: -0.6, size: 0.06, life: 14, gravity: 2 });
      } else if (e.kind === 'vinegar') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 1.2), { n: 14, color: '#e8f0ff', speed: 1.4, up: 0.4, size: 0.07, life: 10 });
      } else if (e.kind === 'roof' && this.er.lanternAt) {
        this.ctx.vfx.burst(this.er.lanternAt.clone().setY(this.er.lanternAt.y + 1.5), { n: 30, color: '#d3dcea', speed: 1.8, up: -0.8, size: 0.08, life: 18, gravity: 1 });
      } else if (e.kind === 'band') {
        this.ctx.vfx.burst(p, { n: 16, color: '#8a8e96', speed: 1.2, up: 0.6, size: 0.12, life: 14, smoke: true });
      } else if (e.kind === 'held' && this.er.candle) {
        this.ctx.flare(this.er.candle, '#ffffff', 2.2, 12);
        this.ctx.vfx.ring(this.er.candle.clone().setY(this.er.candle.y - 0.5), 0.3, 1.8, '#f4f8ff', 10, 0.9);
      } else if (e.kind === 'hugWarn') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.1), 0.6, 2.4, '#ff4f3a', 36, 0.8);
        this.ctx.vfx.burst(p.clone().setY(p.y + 1.4), { n: 20, color: '#120c1c', speed: 1.2, up: 0.8, size: 0.2, life: 30 });
      } else if (e.kind === 'hug') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.1), 0.5, 2.6, '#3a2a5a', 18, 0.9);
        this.ctx.shake(0.3);
      } else if (e.kind === 'split') {
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.1), 0.8, 8.0, '#3a2a5a', 40, 0.9);
      } else if (e.kind === 'hailWarn') {
        // (Chapter 5, the mirrors again: the wind's hail down the hole, on the hero.)
        this.ctx.vfx.ring(p.clone().setY(p.y - 0.1), 0.3, 1.8, '#cfe2ff', 24, 0.8);
      } else if (e.kind === 'hail') {
        this.ctx.vfx.burst(p.clone().setY(p.y + 2.6), { n: 26, color: '#e6eef8', speed: 2.2, up: -1.4, size: 0.08, life: 14, gravity: 3 });
        this.ctx.shake(0.18);
      } else if (e.kind === 'hit' && e.npc === 'door') {
        this.er.barsHit = 1;
      } else if (e.kind === 'dawn') {
        this.er.dawn = 1;
        if (this.er.altarAt) this.ctx.flare(this.er.altarAt, '#ffffff', 6, 24);
        this.ctx.shake(0.3);
      }
    }
  }

  override draw(s: BattleState): void {
    super.draw(s);
    const erm = s.senda?.litter?.erm;
    if (!erm) return;
    // Aud's mirror out of its place until it is set.
    const slot = this.er.pieces[this.er.audSlot];
    if (slot && this.place === 'naveNow') slot.g.visible = erm.aud === 2;
    const k = `${erm.segs.join(',')}|${erm.weak.join('')}`;
    if (k !== this.er.beamKey) {
      this.er.beamKey = k;
      this.rebuildNaveBeam(erm.segs, erm.weak);
      this.aimPieces(s);
    }
    this.er.altarLit = erm.altar ? 1 : 0;
    this.er.doorLit = erm.door ? 1 : 0;
    // The lamps as the sim has them.
    let changed = false;
    erm.lit.forEach((v, i) => {
      if ((this.er.lampLit[i] ?? 0) !== v) {
        this.er.lampLit[i] = v;
        changed = true;
      }
    });
    // Chapter 5: the lamps, the candle, the sun, the frost.
    const vel = erm.vel;
    if (vel) {
      vel.lamp.forEach((x, i) => {
        const on = x === 1 ? 1 : 0;
        if ((this.er.lampLit[i] ?? 0) !== on) {
          this.er.lampLit[i] = on;
          changed = true;
        }
      });
      this.er.candleLit = vel.candle;
      this.er.candleSize = Math.max(0.4, 1 - erm.fast * 0.2);
      this.er.sun = vel.sun === 1 ? 1 : 0;
      if (this.er.frostOn !== vel.frost) {
        this.er.frostOn = vel.frost;
        for (const m of this.er.frost) m.visible = vel.frost >= 0;
      }
    }
    if (changed) this.drawRings();
    if (s.waveT >= erm.wailUntil) this.er.wail = 0;
    // The tongues, as they crawl.
    // (Keyed to ~7 cm: a crawl smaller than that isn't seen, and each rewrite is an upload.)
    const qz = (x: number): number => Math.round(x / 60);
    const tk = erm.tongues.map((t) => {
      const e = s.enemies.find((x) => x.id === t.id);
      return e && e.hp > 0 ? `${qz(e.x)},${qz(e.y)}` : '';
    });
    const b = s.enemies.find((e) => e.id === erm.boss);
    const tkey = `${tk.join('|')}#${b && b.hp > 0 ? `${qz(b.x)},${qz(b.y)}` : ''}`;
    if (tkey !== this.er.tongueKey) {
      this.er.tongueKey = tkey;
      this.rebuildTongues(s);
    }
  }

  /** Each mirror's disc turned to send the beam where it goes; each divider square to it. */
  private aimPieces(s: BattleState): void {
    const erm = s.senda?.litter?.erm;
    const L = this.er.L;
    if (!erm || !L) return;
    const pieces = [...L.mirrors, ...L.splits];
    pieces.forEach((h, i) => {
      const view = this.er.pieces[i];
      if (!view) return;
      const c = hexToWorld(h.q, h.r);
      let inDir: THREE.Vector3 | null = null;
      let outDir: THREE.Vector3 | null = null;
      for (let j = 0; j + 3 < erm.segs.length; j += 4) {
        const ax = erm.segs[j] as number;
        const ay = erm.segs[j + 1] as number;
        const bx = erm.segs[j + 2] as number;
        const by = erm.segs[j + 3] as number;
        if (bx === c.x && by === c.y && !inDir) inDir = new THREE.Vector3(ax - bx, 0, ay - by).normalize();
        if (ax === c.x && ay === c.y && !outDir) outDir = new THREE.Vector3(bx - ax, 0, by - ay).normalize();
      }
      if (inDir && outDir && !view.split) {
        const n = inDir.clone().add(outDir);
        const dir = n.lengthSq() < 1e-4 ? inDir : n;
        view.want = Math.atan2(dir.x, dir.z);
      } else if (inDir && view.split) view.want = Math.atan2(inDir.x, inDir.z) + (erm.splits[i - L.mirrors.length] ? -0.5 : 0.5);
      else view.want = view.split ? (erm.splits[i - L.mirrors.length] ?? 0) * 1.1 + i : ((s.senda?.litter?.turns[i] ?? 0) * Math.PI) / 3 + i * 0.7;
    });
  }

  protected override frameOwn(now: number, dt: number): void {
    if (this.er.shaftMat) (this.er.shaftMat.uniforms.uT as THREE.IUniform<number>).value = now % 200;
    if (this.er.beamMat) (this.er.beamMat.uniforms.uT as THREE.IUniform<number>).value = now % 120;
    if (this.er.tongueMat) (this.er.tongueMat.uniforms.uT as THREE.IUniform<number>).value = now % 120;
    // The sun (chapter 5): the shaft and the beam go white, eased in.
    for (const mat of [this.er.shaftMat, this.er.beamMat]) {
      const u = mat?.uniforms.uSun as THREE.IUniform<number> | undefined;
      if (u) u.value += (this.er.sun - u.value) * Math.min(1, dt * 1.5);
    }
    if (this.er.bars) {
      this.er.barsHit = Math.max(0, this.er.barsHit - dt * 4);
      this.er.bars.position.x += (Math.sin(now * 60) * 0.04 * this.er.barsHit - (this.er.bars.position.x - (this.er.doorAt?.x ?? 0))) * 0.5;
    }
    for (const m of this.er.pieces) {
      let d = m.want - m.yaw;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      m.yaw += d * Math.min(1, dt * 7);
      m.head.rotation.y = m.yaw;
      m.spin = Math.max(0, m.spin - dt * 2);
    }
    const g = this.glow;
    // The lantern under the hole (now): its white flame and a cold halo.
    if (this.er.lantern && !this.er.past) {
      const p = this.er.lantern.position;
      const fl = 0.9 + 0.1 * Math.sin(now * 9.3) + 0.05 * Math.sin(now * 23.1);
      g.add(p.x, p.y, p.z, WHITE, 1.3, 0.95 * fl);
      g.add(p.x, p.y, p.z, COLD, 3.2, 0.25 * fl);
    }
    // The beam's glints at each piece it reaches; the altar and the door lit.
    for (const v of this.er.pieces) if (v.g.visible) g.add(v.at.x, v.at.y, v.at.z, v.split ? COLD : WHITE, 1.2, 0.45 + v.spin * 0.6);
    this.er.both = Math.max(0, this.er.both - dt * 0.6);
    if (this.er.altarAt) {
      const a = this.er.altarAt;
      if (this.er.altarLit) g.add(a.x, a.y, a.z, WHITE, 2.4 + this.er.both * 3, 0.65 + this.er.both * 0.35);
      else if (this.place === 'naveNow') g.add(a.x, a.y, a.z, RED, 0.7, 0.25 + 0.12 * Math.sin(now * 2.4));
    }
    if (this.er.doorAt) {
      const d = this.er.doorAt;
      if (this.er.doorLit) g.add(d.x, d.y, d.z, WHITE, 2.4 + this.er.both * 3, 0.65 + this.er.both * 0.35);
      else if (this.place === 'naveNow') g.add(d.x, d.y, d.z, RED, 0.7, 0.25 + 0.12 * Math.sin(now * 2.4 + 1));
    }
    // The ink on the walls, breathing violet.
    for (const p of this.er.ink) g.add(p.x, p.y, p.z, INK, 1.4, 0.22 + 0.1 * Math.sin(now * 1.7 + p.x));
    // The lamps of the vigil: dark with a dull red ember while out; a warm-white flame, a halo, once lit.
    this.er.lamps.forEach((p, i) => {
      const fl = this.er.lampFlash[i] ?? 0;
      if (fl > 0) this.er.lampFlash[i] = Math.max(0, fl - dt * 0.8);
      if (this.er.lampLit[i]) {
        const f = 0.88 + 0.12 * Math.sin(now * 8 + i * 1.7);
        g.add(p.x, p.y, p.z, FLAME, 0.9, 0.95 * f);
        g.add(p.x, p.y, p.z, WARM, 3.0 + fl * 3, (0.3 + fl * 0.4) * f);
      } else g.add(p.x, p.y, p.z, RED, 0.45, 0.2 + 0.1 * Math.sin(now * 2 + i));
    });
    if (this.er.lampFlash.some((x) => x > 0)) this.drawRings();
    // The wail: the nave's light pulled down (a dark violet over the floor's middle).
    if (this.er.wail > 0 && this.er.L) {
      const c = this.hex(0, 0, 0.5);
      g.add(c.x, c.y, c.z, INK, 9, 0.25 + 0.08 * Math.sin(now * 6));
    }
    // The memory's dawn, white through the roof onto the altar.
    if (this.er.dawn > 0 && this.er.altarAt) {
      const a = this.er.altarAt;
      g.add(a.x, a.y + 1.5, a.z, WHITE, 6, 0.6);
    }
    // Chapter 5: the candle by Tessa (smaller after each wail not cut), the brazier once lit, the sun at the hole.
    if (this.er.candle && this.er.candleLit) {
      const c = this.er.candle;
      const z = this.er.candleSize;
      const f = 0.85 + 0.15 * Math.sin(now * 11) + 0.06 * Math.sin(now * 29);
      g.add(c.x, c.y, c.z, FLAME, 0.5 * z, 0.95 * f);
      g.add(c.x, c.y, c.z, WHITE, 1.9 * z, 0.35 * f);
    }
    if (this.er.brazier && this.er.brazierLit) {
      const b = this.er.brazier;
      const f = 0.85 + 0.15 * Math.sin(now * 7.3);
      g.add(b.x, b.y, b.z, new THREE.Color('#ff8a6e'), 1.2, 0.8 * f);
      g.add(b.x, b.y, b.z, FLAME, 0.6, 0.9 * f);
    }
    if (this.er.sun > 0 && this.er.lanternAt) {
      const l = this.er.lanternAt;
      g.add(l.x, l.y + 3.4, l.z, WHITE, 5.0, 0.55);
    }
    // The refectory's hearth.
    if (this.er.hearth) {
      const h = this.er.hearth;
      const f = 0.85 + 0.15 * Math.sin(now * 7.1) + 0.08 * Math.sin(now * 17.3);
      g.add(h.x, h.y, h.z, new THREE.Color('#ff9a6a'), 2.6, 0.6 * f);
    }
    if (this.er.lantern && this.pl && !this.er.past) {
      const f = 0.92 + 0.08 * Math.sin(now * 9.3);
      this.pl.intensity = this.lightBase * f * (this.er.wail > 0 ? 0.55 : 1);
    }
    void K;
  }
}

/**
 * The Hermitage's atrium (the cleric's chapter 4, act 1): a crypt's descent re-dressed as the Hermitage's halls cut in the
 * rock. The crypt's stone made pale and carved; its runes and flames off; the pilgrims no one buried lying in heaps by
 * the walls (bones, skulls, a staff), niches with a candle stub cut in the walls, ink running down here and there, the
 * snow blown in by the door. Cold white light, never near black (docs/sendas: «nada de escenas casi negras»).
 */
export class AtriumCrypt {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(200);
  private lamps: THREE.Vector3[] = [];
  private ink: THREE.Vector3[] = [];

  constructor(
    private ctx: OmenCtx,
    map: MapDef,
    crypt: { mesh: THREE.Mesh; runes: THREE.Group; flames: { mesh: THREE.Object3D; glow: THREE.Sprite }[]; decor: THREE.Group },
    rooms: THREE.PointLight[],
  ) {
    this.group.name = 'ermita-atrium';
    crypt.runes.visible = false;
    for (const f of crypt.flames) {
      f.mesh.visible = false;
      f.glow.visible = false;
    }
    rooms.forEach((l) => {
      l.color.set(0xd0dcff);
      l.intensity *= 1.15;
    });
    const geo = crypt.mesh.geometry;
    const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (col && pos) {
      const c = new THREE.Color();
      for (let i = 0; i < pos.count; i++) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = pos.getZ(i);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const n = 0.5 + 0.5 * Math.sin(x * 1.9 + z * 1.2) * Math.cos(z * 1.4 - x * 0.7);
        if (lum < 0.04) c.set('#16181d');
        else if (y > 0.5) c.set(STONE[Math.floor(n * 2.99)] as string).multiplyScalar(0.98);
        else if (y > 0.05) c.set(STONE[Math.floor(n * 2.99)] as string).multiplyScalar(0.72 + lum * 0.45);
        else c.set(FLAG[Math.floor(n * 2.99)] as string).multiplyScalar(0.78 + lum * 0.4);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    const L = map.dungeon;
    if (!L) return;
    const R = map.radius;
    const W = 2 * R + 1;
    const floor = new Set<number>();
    for (const h of L.floor) floor.add((h.r + R) * W + (h.q + R));
    const D: [number, number][] = [
      [1, 0],
      [1, -1],
      [0, -1],
      [-1, 0],
      [-1, 1],
      [0, 1],
    ];
    const kit = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const ball = new THREE.SphereGeometry(0.5, 7, 5);
    let k = 0;
    for (const h of L.floor) {
      for (const [dq, dr] of D) {
        const id = (h.r + dr + R) * W + (h.q + dq + R);
        if (floor.has(id)) continue;
        const n = k++;
        if (n % 4 !== 0) continue;
        const a = hexToWorld(h.q, h.r);
        const b = hexToWorld(h.q + dq, h.r + dr);
        const mx = a.x + (b.x - a.x) * 0.42;
        const my = a.y + (b.y - a.y) * 0.42;
        const yaw = Math.atan2(a.x - b.x, a.y - b.y);
        const p = this.ctx.at(mx, my, 0);
        const sel = n % 12;
        if (sel === 0) {
          // A niche with a candle stub.
          const wx = a.x + (b.x - a.x) * 0.5;
          const wy = a.y + (b.y - a.y) * 0.5;
          const w = this.ctx.at(wx, wy, 0);
          kit.add(box, '#14161a', place(w.x, w.y + 0.9, w.z, yaw, new THREE.Vector3(0.4, 0.55, 0.1)));
          kit.add(box, '#d8dce2', place(p.x, p.y + 0.72, p.z, yaw, new THREE.Vector3(0.05, 0.14, 0.05)));
          this.lamps.push(new THREE.Vector3(p.x, p.y + 0.84, p.z));
        } else if (sel === 4) {
          // A pilgrim's bones in a heap by the wall: ribs, a skull, a staff.
          kit.add(ball, '#cfc9be', place(p.x, p.y + 0.1, p.z, yaw, new THREE.Vector3(0.22, 0.2, 0.24)));
          for (let i = 0; i < 4; i++) kit.add(box, '#bdb7ac', place(p.x + Math.sin(yaw + i) * 0.2, p.y + 0.04, p.z + Math.cos(yaw + i) * 0.2, yaw + i * 0.8, new THREE.Vector3(0.36, 0.04, 0.05)));
          kit.add(box, '#4a3e34', place(p.x - 0.2, p.y + 0.03, p.z + 0.1, yaw + 0.6, new THREE.Vector3(1.1, 0.04, 0.05)));
        } else if (sel === 8) {
          // Ink down the wall.
          const wx = a.x + (b.x - a.x) * 0.5;
          const wy = a.y + (b.y - a.y) * 0.5;
          const w = this.ctx.at(wx, wy, 0);
          kit.add(box, '#0c0912', place(w.x, w.y + 0.7, w.z, yaw, new THREE.Vector3(0.3, 1.3, 0.06)));
          this.ink.push(new THREE.Vector3(w.x, w.y + 0.6, w.z));
        }
      }
    }
    box.dispose();
    ball.dispose();
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0.02, flatShading: true });
    this.mats.push(mat);
    const m = kit.build(mat, 'atrium-dress');
    if (m) {
      this.geos.push(m.geometry);
      this.group.add(m);
    }
    this.glow.mesh.frustumCulled = false;
    this.group.add(this.glow.mesh);
  }

  tick(_s: BattleState): void {}

  frame(now: number): void {
    const g = this.glow;
    g.begin();
    this.lamps.forEach((p, i) => {
      const f = 0.85 + 0.15 * Math.sin(now * 8 + i * 1.3);
      g.add(p.x, p.y, p.z, FLAME, 0.6, 0.9 * f);
      g.add(p.x, p.y, p.z, WARM, 2.2, 0.22 * f);
    });
    for (const p of this.ink) g.add(p.x, p.y, p.z, INK, 1.2, 0.2 + 0.08 * Math.sin(now * 1.7 + p.x));
    g.end();
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

/** The look of the cleric's chapter 4 nightmares: the Doliente (a wraith in the sim) and her tongues (draggers). */
export const ermitaSkin = (s: BattleState, e: { id: number; kind: string }): string | null => {
  const m = s.senda?.litter?.erm;
  if (!m) return null;
  if (e.id === m.boss && (s.rules?.senda?.litter?.hermit?.vigil || s.rules?.senda?.litter?.hermit?.vela?.mourn)) return 'doliente';
  if (m.tongues.some((t) => t.id === e.id)) return 'lengua';
  return null;
};
